import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { VaultApi } from "../../api/vaultApi";
import "@internetarchive/ads-button";

@customElement("tos-agreement")
export class TosAgreement extends LitElement {
  @property({ type: Number }) userId: number | undefined;

  // whether the user has clicked "Yes" agreeing to Terms of Service (TOS)
  @state() isTosAgreementChecked: boolean = false;

  protected onClickCheckbox(e: Event) {
    const { checked } = e.target as HTMLInputElement;
    this.isTosAgreementChecked = checked;
  }

  protected async onClickConfirm() {
    if (this.isTosAgreementChecked && this.userId !== undefined) {
      // TODO: how do we make the new Date match the server timestamp ...
      await VaultApi.updateUser(this.userId, { tosLastAgreedDate: new Date() });
      // refresh the page - now w/o TOS
      window.location.reload();
    }
  }

  render(): TemplateResult {
    return html`
      <div type class="outer-container">
        <div class="tos-contents">
          <div class="tos-header">
            <h2>Vault Service Agreement</h2>
            <h3>Terms and Conditions</h3>
          </div>
          <p>
            Subject to the terms of this Agreement, Internet Archive provides
            the Service (as further described and defined in Exhibit A attached
            hereto) to Institution.
          </p>
          <p>
            This Agreement, including the Terms and Conditions, Order Form, and
            Exhibit A (attached hereto), constitutes the entire agreement
            between the parties pertaining to the subject matter hereof, and any
            and all agreements, written or oral, previously existing between the
            parties relating to such subject matter are expressly superseded.
          </p>
          <p>
            By accepting this Agreement, clicking “Agree,” or similarly named
            button, Institution agrees to the terms of this Agreement and
            represents that the person accepting this Agreement has the
            authority to bind Institution to this Agreement.
          </p>
          <ol>
            1.
            <label>Use of Service.</label>
            <li class="indent">
              (a) Subject to the terms and conditions of this Agreement,
              Internet Archive hereby grants to Institution during the Term a
              non-exclusive, non-transferable right to access and use the
              Service solely to create and use Collections, as further described
              and defined in Exhibit A, solely for Institution’s own internal
              purposes.
            </li>

            <li class="indent">
              (b) Institution acknowledges and agrees that Internet Archive
              retains all right, title and interest in and to the Service and
              all intellectual property rights contained therein. All rights not
              expressly granted to Institution under this Agreement are
              expressly reserved to Internet Archive.
            </li>

            2.
            <label>Access of Service by Users.</label>
            <li class="indent">
              Institution may authorize up to ten (10) individuals to access the
              Service (“<span class="bold">Users</span>"). Users must be
              employed faculty, researchers, and/or staff at the Institution or
              officially affiliated researchers. Internet Archive will provide
              Institution with a unique username and password to enable
              Institution to access the administrator account for the Service.
              Institution is responsible for establishing usernames and
              passwords for its Users and for maintaining the confidentiality of
              all usernames and passwords, and is solely responsible for all
              activities that occur under any of its or its Users’ usernames and
              passwords, including, but not limited to, the username and
              password for the Institution’s administrator account.Institution
              agrees (a) not to allow a third party to use its administrator
              username or password at any time; and (b) notify Internet Archive
              promptly of any actual or suspected unauthorized use of any
              usernames or passwords, or any other breach or suspected breach of
              this Agreement. Internet Archive reserves the right to terminate
              any username and password, which Internet Archive reasonably
              determines may have been used by an unauthorized third party or by
              any User or individual other than the User to whom such username
              and password was originally assigned.
            </li>

            3.
            <label>Creation of Collections.</label>
            <li class="indent">
              (a) Institution will be solely responsible for creating,
              reviewing, editing, and otherwise controlling any Collections. All
              content provided by Institution or any of its Users for the
              Collections (the “Content”) may be used, distributed, copied,
              displayed and modified by Internet Archive to provide the Service
              and to host the Collections Without limiting the generality of the
              first sentence of this Section 3(a), Institution is responsible
              for ensuring that it has all rights necessary to allow Internet
              Archive to use the Content for these purposes.
            </li>

            <li class="indent">
              (b) Institution acknowledges that Internet Archive has no
              obligation to Institution or any third party, and undertakes no
              responsibility, to review the Collections to determine whether the
              Collections may incur liability to third parties. Notwithstanding
              anything to the contrary in this Agreement, if Internet Archive
              believes in its sole discretion that any materials in the
              Collections may create liability for Internet Archive and/or
              Institution, Internet Archive has the right to take any actions
              with respect to the Website and the Collections to minimize or
              eliminate this liability, including, but not limited to, removing
              any or all Collections (or any portions thereof) from the Website.
              Internet Archive will inform Institution of such actions, but will
              not be required to get any approval from Institution or any third
              party, neither before, nor after taking such actions. Institution
              shall comply with any local, national, and/or international laws
              and regulations that govern the Content.
            </li>

            4.
            <label>Availability of Service and Website.</label>
            <li class="indent">
              Internet Archive will use commercially reasonable efforts to make
              the Service available to Institution and to host the Website on
              Internet Archive’s or its contractor’s servers and to make any
              Collections created under this Agreement publicly available on the
              Website. Internet Archive will use reasonable efforts to inform
              Institution if the Website and/or Service is not available.
            </li>

            5.
            <label>Fees and Payment.</label>
            <li class="indent">
              Vault subscription fees will be set forth in the invoice, based
              upon the Order Form. Except as otherwise provided in the Order
              Form, Institution will pay all fees and other charges due and
              payable to Internet Archive within thirty (30) days after the date
              of Internet Archive’s invoice. If exempt from sales tax,
              Institution shall provide Internet Archive with a copy of its
              exemption certificate. If Institution is not able to prove
              exemption to Internet Archive, Institution shall be responsible
              for payment of any sales, use, value-added and other taxes, and
              all fees or assessments levied by any governmental body to which
              Institution’s payments with respect to the Services may be
              subject.
            </li>

            6.
            <label>Term and Termination</label>
            <li class="indent">
              This Agreement commences on the Effective Date and remains in
              effect for a period of twelve (12) months (the “Initial Term”) and
              shall automatically renew for successive twelve (12) month periods
              (each a “Renewal Term”) nless either party provides the other
              party with notice of non-renewal at least thirty (30) days prior
              to the end of the Initial Term or the then-current Renewal Term.
              Internet Archive may adjust the fees to be paid by Institution for
              each Renewal Term upon issuing a written notice to Institution at
              least sixty (60) days before the beginning of the applicable
              Renewal Term. The Initial Term and any Renewal Terms shall be
              collectively referred to as the “Term”. Either party may terminate
              this Agreement (a) for convenience upon thirty (30) days written
              notice to the other party; or (b) immediately upon written notice
              to the other party if the other party materially breaches this
              Agreement, and such breach remains uncured more than thirty (30)
              days after the breaching party’s receipt of written notice of such
              breach. Internet Archive may suspend or terminate Institution’s
              access to the Service at any time without prior notice in order
              to: (a) prevent damages to, or degradation of, Internet Archive’s
              Internet network integrity; (b) comply with any law, regulation,
              court order, or other governmental liability resulting from
              Institution’s use of or access to the Service; or (c) if Internet
              Archive deems such suspension or termination necessary to avoid
              liability. Sections 1(b), 3, 6, 7, 8, and 9 and any payment
              obligations incurred prior to the suspension, termination or
              expiration shall survive the suspension, termination or expiration
              of this Agreement.
            </li>

            7.
            <label>Disclaimer of Warranties.</label>
            <li class="indent">
              The services described in this agreement are provided to
              Institution on an “as is” basis without warranties of any kind.
              Without limiting the foregoing, Internet Archive disclaims all
              warranties and representations of any kind, whether express,
              implied, or statutory, including without limitation the implied
              warranties of merchantability, title, non-infringement, and
              fitness for a particular purpose. Internet Archive does not
              warrant that the operation of the service and/or the website shall
              be uninterrupted. In the event of an interruption of the operation
              of the service and/or the website, Internet Archive’s sole
              obligation and Institution's sole remedy shall be to restore
              operation as soon as reasonably possible.
            </li>

            8.
            <label>Limitation of Liability.</label>
            <li class="indent">
              Except for any Indemnification Obligations under Section 9
              (Indemnification), in no event shall either party be liable to the
              other for indirect, incidental, special or other consequential
              damages, including without limitation damages for loss of profits,
              data or use, incurred by either party or any third party, arising
              out of or related to this agreement whether in an action in
              contract, tort, or otherwise, even if the other party has been
              advised of the possibility of such damages. except for any
              Indemnification Obligations under Section 9 (Indemnification),
              neither party’s total cumulative liability arising from or related
              to this agreement, whether in contract or tort or otherwise, will
              exceed the aggregate amount paid to Internet Archive by
              Institution pursuant to Section 5 (Fees and Payment) in the twelve
              (12) month period immediately preceding the event first giving
              rise to liability. This limitation is cumulative and will not be
              increased by the existence of more than one incident or claim.The
              foregoing limitation shall not apply to Institution’s payment
              obligations under this agreement. The parties agree that the
              limitations of liability set forth in this Section 8 shall survive
              and continue in full force and effect despite any failure of
              consideration or of any exclusive remedy. The parties acknowledge
              that the prices have been set and the Agreement entered into in
              reliance upon these limitations of liability and that all such
              limitations form an essential basis of the bargain between the
              parties. 9. Indemnification. Institution shall, upon request,
              indemnify, defend, and hold harmless Internet Archive and its
              affiliates, and eac of their respective employees, directors and
              representatives, from and against any and all claims, costs,
              losses, damages, liabilities, judgments, penalties and expenses
              (including reasonable fees of attorneys and other professionals),
              arising out of or related to (a) Institution’s and its Users’ use
              of and access to the Service, including any Collections generated
              from the use of the Service; or (b) any improper or unauthorized
              use of the Service by Institution or its Users.
            </li>

            9.
            <label>Indemnification</label>
            <li>
              Institution shall, upon request, indemnify, defend, and hold
              harmless Internet Archive and its affiliates, and each of their
              respective employees, directors and representatives, from and
              against any and all claims, costs, losses, damages, liabilities,
              judgments, penalties and expenses (including reasonable fees of
              attorneys and other professionals), arising out of or related to
              (a) Institution’s and its Users’ use of and access to the Service,
              including any Collections generated from the use of the Service;
              or (b) any improper or unauthorized use of the Service by
              Institution or its Users.
            </li>

            10.
            <label>General</label>
            <li>
              10.1 <b>Governing Law</b>. This Agreement, and any and all actions
              arising from or in any manner affecting the interpretation of this
              Agreement, will be governed by, and construed solely in accordance
              with, the laws of the State of California, (unless Institution is
              prohibited by law from agreeing to allowing the laws of the State
              of California to govern Agreement) without giving effect to any
              conflicts of laws principles that would require the application of
              the laws of a different state.
            </li>
            <li></li>
            <li>
              10.2 <b>Assignment.</b> Institution may not assign this Agreement
              to any third party without Internet Archive’s prior written
              consent. Any purported assignment in derogation of the foregoing
              will be null and void.
            </li>
            <li>
              10.3 <b>Informal Resolution.</b> Institution will bring issues
              that may lead to a dispute to the attention of Internet Archive
              immediately. Before filing a claim, Internet Archive and
              Institution agree to try to resolve the dispute by providing
              notice to the other party and having the appropriate executives
              attempt to resolve the dispute through communication and
              discussion. After fifteen (15) days, either party may bring a
              formal proceeding.
            </li>
            <li>
              10.4 <b>Arbitration.</b> The parties agree that all disputes
              arising under this Agreement shall be arbitrated in accordance
              with and subject to the Commercial Arbitration Rules of the
              American Arbitration Association (“AAA”) then in effect.
              Arbitration shall commence upon written notice of demand for
              arbitration by the party seeking arbitration, setting forth the
              specifics of the matter in controversy or the claim being made.
              The arbitration shall be heard before a single arbitrator in San
              Francisco, California in the English language. The arbitrator will
              be selected by mutual agreement of the parties from a panel of
              persons having experience with and knowledge of the information
              technology, and will be an attorney licensed to practice in
              California.
            </li>
            <li>
              10.5 <b>Notices.</b> All notices provided under this Agreement
              will be in writing and will be delivered by personal delivery,
              private courier, or by certified or registered mail, return
              receipt requested, and are deemed given when received. Notices to
              Institution may also be sent to the applicable administrator
              account email address and are deemed given when sent (or when
              delivery is refused). Notices to Internet Archive must be sent to
              Internet Archive, Attn: Legal Department, 300 Funston, San
              Francisco, CA 94118 with a copy to vault@archive.org.
            </li>

            <li>
              10.6 <b>Waiver.</b> No waiver of any terms or conditions of this
              Agreement will be valid or binding on a party unless such party
              makes the waiver in writing.
            </li>
            <li>
              10.7 <b>Agreement Modification.</b> Internet Archive may modify
              this Agreement periodically. If a modification is material, then
              Internet Archive will notify Institution prior to the
              modification's effective date . Otherwise, modifications to this
              Agreement will be posted on the Website. Modifications to this
              Agreement will be effective thirty (30) days after such posting on
              the Website, provided that any material modifications will be
              effective thirty (30) days after Internet Archive sends
              Institution notice of such modifications. By continuing to use the
              Services after such date, Institution agrees to be bound by the
              modified Agreement. If Institution does not agree to the modified
              Agreement for the Services, Institution may terminate the Services
              upon written notice within thirty (30) days of receiving notice of
              the change.
            </li>
            <li>
              10.8 <b>Severability.</b> If any provision of this Agreement is
              found or held to be invalid or unenforceable, then the meaning of
              such provision will be construed so as to render the provision
              enforceable, and if no feasible interpretation would save such
              provision, it will be severed from the remainder of this
              Agreement, which will remain in full force and effect.
            </li>
            <li>
              10.9 <b>Force Majeure.</b> Neither party shall be considered in
              default of its performance under this Agreement to the extent such
              performance is prevented, restricted or interfered with by any act
              or condition whatsoever beyond the reasonable control of such
              party.
            </li>
            <li>
              10.10 <b>Relationship of the Parties.</b> Institution’s
              relationship to Internet Archive is that of an independent
              contractor, and neither party is an agent or partner of the other.
              This Agreement may be executed in counterparts, each of which will
              be considered an original, but all of which together will
              constitute one and the same instrument.
            </li>
          </ol>
          <h2>Exhibit A</h2>
          <ol>
            <label>1. Description of Service. </label>
            <li>
              <b>1.1 The “Vault Service”.</b> Internet Archive has developed a
              web application to provide preservation of digital assets. This
              service, named “Vault”, is a web-based application designed for
              organizations interested in archiving, accessing and managing
              digital assets without needing specific technical expertise or
              resources (the “<b>Service</b>”).
            </li>
            <li>
              <b>1.2 Collections.</b> The Service allows Users to login and
              create collections (“<b>Collections</b>”), catalogue the digital
              assets associated with a Collection, monitor the preservation
              process, search and browse the Collections when complete, and
              access these Collections. Internet Archive will host any and all
              Collections created under this Agreement.
            </li>
            <li>
              <b>1.3 Website Access to Collections.</b> By default the Service
              will make the Collections available under the following URL:
              <i>http//vault.archive-it.org</i> or such other URL that Internet
              Archive may use in the future (the “<b>Website</b>”). Access to a
              Collection (either part or all) can be restricted and/or made not
              publicly available.
            </li>
            <li>
              <b>1.4 Management of Collected Data.</b> All data and information
              collected through the Service will be stored at one of the
              Internet Archive’s data centers. The Collections created through
              the Service and associated content from websites will be kept in a
              separate repository from Internet Archive’s general archive at
              <a target="_blank" href="https://www.archive.org"
                >www.archive.org</a
              >. A primary and backup copy of this data will be made available
              online.
            </li>
            <li>
              <b
                >1.5 Additional Terms and Conditions for Vault Basic and
                Sponsored Accounts.</b
              >
              Institution is responsible for all collection building, uploading,
              managing, metadata and related account maintenance activities, and
              agrees to maintain the Collection(s) with regular oversight.
              Internet Archive generally will not provide individual technical
              support to Vault Sponsored accounts related to the archiving or
              preservation of content in their collections, however Institution
              will have unlimited access to technical and training documentation
              at
              <a href="https://vault-webservices.zendesk.com/hc/en-us">
                https://vault-webservices.zendesk.com/hc/en-us
              </a>
              and any systemwide technical fixes or improvements pertaining to
              the core functionality of the web application are implemented for
              all subscription types within the Vault service.
            </li>
            <li>
              <b>1.6 Communications Opt-In.</b>
              By using Vault, Institution consents to receive communications
              from the Internet Archive related to account, service updates, and
              other information relevant to the use of the service.
              Institution’s users may manage communication preferences or opt
              out of non-required messages at any time.
            </li>
          </ol>
        </div>
        <div class="bottom-agreement">
          <span>
            <input
              .checked=${this.isTosAgreementChecked}
              type="checkbox"
              @change=${this.onClickCheckbox}
              id="tos-checkbox"
            />
            <label for="tos-checkbox"
              >I agree to these Terms and Conditions.</label
            >
          </span>
          <ads-button
            @click=${this.onClickConfirm}
            .disabled=${!this.isTosAgreementChecked}
            >Confirm</ads-button
          >
        </div>
      </div>
    `;
  }

  static styles = css`
    :host {
      line-height: 22px;
    }

    a {
      color: var(--vault-light-blue);
    }

    h2,
    h3 {
      text-align: center;
    }

    h2 {
      padding-top: 12px;
    }

    h3 {
      padding-bottom: 18px;
    }

    ol {
      margin: 0;
      font-weight: bold;
    }

    ol > label {
      display: inline-block;
      text-decoration: underline;
      padding-top: 8px;
    }

    li {
      list-style: none;
      font-weight: normal;
      text-indent: 24px;
      /* margin to match default p styles */
      margin-top: 1em;
      margin-bottom: 1em;
    }

    .outer-container {
      height: calc(var(--vault-page-content-height) - 5vh);
      width: 100vw;
      background: #ddd;
      display: flex;
      flex-direction: column;
      padding-top: 5vh;
    }

    .tos-contents,
    .bottom-agreement {
      width: 52vw;
      margin: 0 auto;
      overflow: auto;
      padding: 24px;
      background: white;
      border-radius: 15px;
    }

    .tos-contents {
      height: 70vh;
    }

    .bottom-agreement {
      margin-top: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    span.bold {
      font-weight: bold;
    }
  `;
}
